/**
 * Cloud mode RoomProxy implementation.
 *
 * Makes HTTP calls to the worker for Room methods,
 * and maintains a WebSocket connection for broadcasts.
 */

import { ROOM_CLIENT_ID_HEADER, type Room, type PublicRoomState } from '@merels/room/room.types'
import type { BroadcastListener } from '@merels/room'
import type { GameSetup, SetupReadiness } from '@merels/room/gameSetup.types'
import type { GameSavedOptions } from '@merels/game-logic/types/options'
import type { RoomProxy, RoomBroadcast } from './roomProxy.types'

import type { AuthDep } from '../../auth/auth.types'

/**
 * The runtime pieces `createCloudRoomProxy` needs that aren't ambient
 * globals in every environment — a browser tab has both `WebSocket` and
 * `fetch` on `window`, but a headless Node client (see
 * apps/web/tests/step_definitions_headless/world.ts) has neither and must
 * inject a `ws`-backed WebSocket constructor (Node's own `fetch` is global
 * since Node 18, so only `WebSocket` is ever actually overridden today).
 */
export interface CloudRoomProxyEnv {
  WebSocket: typeof WebSocket
  fetch: typeof fetch
}

// `fetch` must stay bound to `globalThis` — calling it as `env.fetch(...)`
// invokes it with `this === env`, and native `fetch` throws "Illegal
// invocation" for any receiver that isn't the real global object. `WebSocket`
// doesn't need this: it's only ever invoked via `new env.WebSocket(...)`,
// which binds its own `this` to the new instance regardless of how the
// constructor reference was stored.
const defaultEnv: CloudRoomProxyEnv = { WebSocket, fetch: fetch.bind(globalThis) }

interface CloudRoomProxyConfig {
  roomId: string
  workerUrl: string
  clientId: string
  auth?: AuthDep
  env?: CloudRoomProxyEnv
}

export class RoomClosedError extends Error {
  readonly code = 'ROOM_CLOSED'

  constructor(readonly opensAt: string | null) {
    super('This room is currently closed.')
    this.name = 'RoomClosedError'
  }
}

/**
 * Create a cloud mode RoomProxy.
 *
 * This creates a proxy that:
 * - Makes HTTP requests to the worker for all Room methods
 * - Maintains a WebSocket connection for server-push broadcasts
 */
export function createCloudRoomProxy(config: CloudRoomProxyConfig): RoomProxy {
  const { roomId, workerUrl, clientId } = config
  const env = config.env ?? defaultEnv
  const baseUrl = `${workerUrl}/api/room/${encodeURIComponent(roomId)}`

  const listeners = new Set<BroadcastListener>()
  let socket: WebSocket | null = null
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  let isDestroyed = false

  const checkDestroyed = () => {
    if (isDestroyed) {
      throw new Error('RoomProxy has been destroyed')
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // WebSocket connection for broadcasts
  // ─────────────────────────────────────────────────────────────────────────────

  async function connectWebSocket(): Promise<void> {
    if (socket?.readyState === env.WebSocket.OPEN || socket?.readyState === env.WebSocket.CONNECTING) {
      return
    }

    let wsUrl = baseUrl.replace(/^http/, 'ws') + `/websocket?clientId=${encodeURIComponent(clientId)}`
    if (config.auth) {
      const token = await config.auth.getToken()
      if (token) {
        wsUrl += `&token=${encodeURIComponent(token)}`
      }
    }
    socket = new env.WebSocket(wsUrl)

    socket.onmessage = (event) => {
      //console.log('[CloudRoomProxy] WebSocket received message:', event.data)
      try {
        const message = JSON.parse(event.data) as RoomBroadcast
        // Log game state updates
        if (message.type === 'roomState') {
          // const { data } = message
          // console.log('[CloudRoomProxy] WebSocket received roomState:', {
          //   hasActiveGame: !!data.activeGame,
          //   gameId: data.activeGame?.gameId,
          //   matesCount: data.mates?.length,
          // })
        } else if (message.type === 'gameState') {
          // const { data } = message
          // const ctx = data.game?.ctx as Record<string, unknown> | undefined
          // console.log('[CloudRoomProxy] WebSocket received gameState:', {
          //   hasGameState: !!data.game?.gameState,
          //   currentPlayer: ctx?.currentPlayer,
          //   gameover: ctx?.gameover,
          // })
        } else {
          // console.log('[CloudRoomProxy] WebSocket received message:', message.type)
        }
        listeners.forEach(listener => {
          try {
            listener(message)
          } catch (error) {
            console.error('[CloudRoomProxy] Error in broadcast listener:', error)
          }
        })
      } catch (error) {
        console.error('[CloudRoomProxy] Error parsing WebSocket message:', error)
      }
    }

    socket.onclose = () => {
      // Attempt to reconnect after a delay
      if (listeners.size > 0 && !isDestroyed) {
        reconnectTimeout = setTimeout(() => connectWebSocket(), 3000)
      }
    }

    socket.onerror = (error) => {
      // During teardown we may intentionally hard-close sockets in Node (`ws`),
      // which can emit an error like "closed before the connection was established".
      if (isDestroyed) return
      console.error('[CloudRoomProxy] WebSocket error:', error)
    }
  }

  function disconnectWebSocket(): void {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout)
      reconnectTimeout = null
    }
    if (socket) {
      const toClose: any = socket
      socket = null

      // In browsers, `close()` is sufficient.
      // In Node (ws), `close()` can leave the process alive if the close handshake
      // never completes, so we fall back to `terminate()` when available.
      // Also, `ws` emits an error if `close()` is called while still CONNECTING,
      // which can crash Node if unhandled. Prefer `terminate()` in that case.
      const readyState: number | undefined = typeof toClose.readyState === 'number' ? toClose.readyState : undefined
      if (readyState === 0 && typeof toClose.terminate === 'function') {
        try {
          // Make sure the inevitable error event is handled (ws maps this to `onerror`).
          toClose.onerror = () => {}
        } catch {
          // ignore
        }
        try {
          toClose.terminate()
        } catch {
          // ignore
        }
        return
      }

      try {
        // Ensure `ws` error events are always handled during teardown.
        toClose.onerror = () => {}
        toClose.onclose = () => {}
        toClose.onmessage = () => {}
      } catch {
        // ignore
      }
      try {
        toClose.close()
      } catch {
        // ignore
      }
      if (typeof toClose.terminate === 'function') {
        try {
          toClose.terminate()
        } catch {
          // ignore
        }
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // HTTP helpers
  // ─────────────────────────────────────────────────────────────────────────────

  async function httpGet<T>(path: string): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', [ROOM_CLIENT_ID_HEADER]: clientId }
    if (config.auth) {
      const token = await config.auth.getToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }
    const response = await env.fetch(`${baseUrl}${path}`, {
      method: 'GET',
      headers,
    })
    if (!response.ok) {
      let errMsg = `HTTP error ${response.status}`
      try {
        const body = await response.json()
        if (body && typeof body === 'object' && (body as any).code === 'ROOM_CLOSED') {
          throw new RoomClosedError(typeof (body as any).opensAt === 'string' ? (body as any).opensAt : null)
        }
        if (body && typeof body === 'object' && 'error' in body) {
          errMsg = String(body.error)
        }
      } catch (error) {
        if (error instanceof RoomClosedError) throw error
      }
      throw new Error(errMsg)
    }
    return response.json()
  }

  async function httpPost<T>(path: string, body: Record<string, unknown> = {}): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', [ROOM_CLIENT_ID_HEADER]: clientId }
    if (config.auth) {
      const token = await config.auth.getToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }
    const response = await env.fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      let errMsg = `HTTP error ${response.status}`
      try {
        const body = await response.json()
        if (body && typeof body === 'object' && (body as any).code === 'ROOM_CLOSED') {
          throw new RoomClosedError(typeof (body as any).opensAt === 'string' ? (body as any).opensAt : null)
        }
        if (body && typeof body === 'object' && 'error' in body) {
          errMsg = String(body.error)
        }
      } catch (error) {
        if (error instanceof RoomClosedError) throw error
      }
      throw new Error(errMsg)
    }
    return response.json()
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Room interface implementation
  // ─────────────────────────────────────────────────────────────────────────────

  const startGame: Room['startGame'] = async (args) => {
    checkDestroyed()
    const { gameId, ...rest } = args
    return httpPost('/startGame', { gameId, ...rest })
  }

  const endGame: Room['endGame'] = async () => {
    checkDestroyed()
    return httpPost('/endGame')
  }

  // Server-internal in normal operation — GameCatalogDO pushes new versions
  // directly into RoomDO's /notifyGameVersion route, not through a client.
  // Exposed here only for RoomProxy/Room interface parity; reuses the same
  // route, which is safe to call from either caller.
  const updateGameVersion: Room['updateGameVersion'] = async (args) => {
    checkDestroyed()
    return httpPost('/notifyGameVersion', args)
  }

  const setActiveGameVersion: Room['setActiveGameVersion'] = async (args) => {
    checkDestroyed()
    return httpPost('/setActiveGameVersion', args)
  }

  const getRoomState: Room['getRoomState'] = async () => {
    checkDestroyed()
    return httpGet<PublicRoomState>('/getRoomState')
  }

  const updateMates: Room['updateMates'] = async (args) => {
    checkDestroyed()
    const { clientId: clientIdParam, mates } = args
    return httpPost('/updateMates', { clientId: clientIdParam, mates })
  }

  const flushMates: Room['flushMates'] = async () => {
    checkDestroyed()
    return httpPost('/flushMates')
  }

  const deleteThumbnailFromHistory: Room['deleteThumbnailFromHistory'] = async (args) => {
    checkDestroyed()
    return httpPost('/deleteThumbnailFromHistory', args)
  }

  const makeMove: Room['makeMove'] = async (args) => {
    checkDestroyed()
    const { move, mateId, stateHash, asHost } = args
    return httpPost('/makeMove', { move, mateId, stateHash, asHost })
  }

  const forceGameState: Room['forceGameState'] = async (args) => {
    checkDestroyed()
    return httpPost('/forceGameState', args)
  }

  const openGame: Room['openGame'] = async (args) => {
    checkDestroyed()
    const { gameId, clientId: clientIdParam, ...options } = args
    return httpPost('/openGame', { gameId, clientId: clientIdParam, ...options })
  }

  const choosePlayerCount: Room['choosePlayerCount'] = async (args) => {
    checkDestroyed()
    const { count } = args
    return httpPost('/choosePlayerCount', { count })
  }

  const updateMateAssignment: Room['updateMateAssignment'] = async (args) => {
    checkDestroyed()
    const { mateId, slotIndex, clientId: clientIdParam } = args
    return httpPost('/updateMateAssignment', { mateId, slotIndex, clientId: clientIdParam })
  }

  const startGameFromSetup: Room['startGameFromSetup'] = async () => {
    checkDestroyed()
    return httpPost('/startGameFromSetup')
  }

  const cancelGameSetup: Room['cancelGameSetup'] = async () => {
    checkDestroyed()
    return httpPost('/cancelGameSetup')
  }

  const getGameSetup: Room['getGameSetup'] = async () => {
    checkDestroyed()
    return httpGet<{ setup: GameSetup; readiness: SetupReadiness } | null>('/getGameSetup')
  }

  const getSavedGameOptions: Room['getSavedGameOptions'] = async (args) => {
    checkDestroyed()
    const { gameId } = args
    return httpGet<GameSavedOptions>(`/getSavedGameOptions/${encodeURIComponent(gameId)}`)
  }

  const getThumbnailHistory: Room['getThumbnailHistory'] = async (args) => {
    checkDestroyed()
    const { persistentId } = args
    return httpGet<{ history: string[] }>(`/getThumbnailHistory/${encodeURIComponent(persistentId)}`)
  }

  const setSavedGameOptions: Room['setSavedGameOptions'] = async (args) => {
    checkDestroyed()
    const { gameId, scope, values, playerCount } = args
    return httpPost('/setSavedGameOptions', { gameId, scope, values, playerCount })
  }

  const restartGame: Room['restartGame'] = async (args) => {
    checkDestroyed()
    return httpPost('/restartGame', args || {})
  }

  const getGameChannelPreference: Room['getGameChannelPreference'] = async (args) => {
    checkDestroyed()
    const { gameId } = args
    return httpGet<{ channel: string | null }>(`/getGameChannelPreference/${encodeURIComponent(gameId)}`)
  }

  const setGameChannelPreference: Room['setGameChannelPreference'] = async (args) => {
    checkDestroyed()
    return httpPost('/setGameChannelPreference', args)
  }

  const getChallengeConfigurationPreference: Room['getChallengeConfigurationPreference'] = async (args) => {
    checkDestroyed()
    return httpGet<{ challengeConfiguration: import('@merels/room').ChallengeConfiguration | null }>(`/getChallengeConfigurationPreference/${encodeURIComponent(args.gameId)}`)
  }

  const setChallengeConfigurationPreference: Room['setChallengeConfigurationPreference'] = async (args) => {
    checkDestroyed()
    return httpPost('/setChallengeConfigurationPreference', args)
  }

  const setGamelessMode: Room['setGamelessMode'] = async (args) => {
    checkDestroyed()
    return httpPost('/setGamelessMode', args)
  }

  const setHost: Room['setHost'] = async (args) => {
    checkDestroyed()
    return httpPost('/setHost', args)
  }

  const setManagedMode: Room['setManagedMode'] = async (args) => {
    checkDestroyed()
    return httpPost('/setManagedMode', args)
  }

  const setHostTerm: Room['setHostTerm'] = async (args) => {
    checkDestroyed()
    return httpPost('/setHostTerm', args)
  }

  const setCuratedMode: Room['setCuratedMode'] = async (args) => {
    checkDestroyed()
    return httpPost('/setCuratedMode', args)
  }

  const setCuratedGames: Room['setCuratedGames'] = async (args) => {
    checkDestroyed()
    return httpPost('/setCuratedGames', args)
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Connection & Lifecycle Methods
  // ─────────────────────────────────────────────────────────────────────────────

  const onMessage: Room['onMessage'] = (listener: BroadcastListener): (() => void) => {
    checkDestroyed()
    listeners.add(listener)

    // Connect WebSocket on first listener
    if (listeners.size === 1) {
      connectWebSocket()
    }

    return () => {
      listeners.delete(listener)
      // Disconnect WebSocket when no more listeners
      if (listeners.size === 0) {
        disconnectWebSocket()
      }
    }
  }

  return {
    destroyProxy: () => {
      if (isDestroyed) return
      isDestroyed = true
      listeners.clear()
      disconnectWebSocket()
    },

    startGame,
    endGame,
    updateGameVersion,
    setActiveGameVersion,
    getRoomState,
    updateMates,
    flushMates,
    getThumbnailHistory,
    deleteThumbnailFromHistory,
    makeMove,
    forceGameState,
    openGame,
    choosePlayerCount,
    updateMateAssignment,
    startGameFromSetup,
    cancelGameSetup,
    getGameSetup,
    getSavedGameOptions,
    setSavedGameOptions,
    restartGame,
    getGameChannelPreference,
    setGameChannelPreference,
    getChallengeConfigurationPreference,
    setChallengeConfigurationPreference,
    setGamelessMode,
    setHost,
    setManagedMode,
    setHostTerm,
    setCuratedMode,
    setCuratedGames,

    onMessage,
  }
}
